import {
  useCreateRoom,
  useRoomTypes,
  useUpdateRoom,
} from '@hooks/accommodation';
import { DropdownSelect } from '@components/common/DropdownSelect';
import { X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { AccommodationRoom } from '../../../../types/accommodation';

interface Props {
  propertyId: string;
  editing: AccommodationRoom | null;
  selectedRoomTypeId?: string | null;
  onClose: () => void;
}

export const RoomFormModal: React.FC<Props> = ({
  propertyId,
  editing,
  selectedRoomTypeId,
  onClose,
}) => {
  const { data: allRoomTypes = [] } = useRoomTypes(propertyId);
  const roomTypes = useMemo(
    () => allRoomTypes.filter((rt) => rt.is_active),
    [allRoomTypes],
  );
  const createRoom = useCreateRoom();
  const updateRoom = useUpdateRoom();
  const isSubmitting = createRoom.isPending || updateRoom.isPending;

  const [form, setForm] = useState({
    room_number: '',
    room_type_id: '',
    floor: '',
    notes: '',
  });

  useEffect(() => {
    if (editing) {
      setForm((currentForm) => {
        const nextForm = {
          room_number: editing.room_number,
          room_type_id: editing.room_type_id,
          floor: editing.floor ?? '',
          notes: editing.notes ?? '',
        };

        return currentForm.room_number === nextForm.room_number &&
          currentForm.room_type_id === nextForm.room_type_id &&
          currentForm.floor === nextForm.floor &&
          currentForm.notes === nextForm.notes
          ? currentForm
          : nextForm;
      });
    } else if (roomTypes.length > 0) {
      const defaultRoomTypeId =
        selectedRoomTypeId &&
        roomTypes.some((roomType) => roomType.id === selectedRoomTypeId)
          ? selectedRoomTypeId
          : roomTypes[0].id;

      setForm((currentForm) =>
        currentForm.room_type_id === defaultRoomTypeId
          ? currentForm
          : { ...currentForm, room_type_id: defaultRoomTypeId },
      );
    }
  }, [editing, roomTypes, selectedRoomTypeId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      room_number: form.room_number.trim(),
      room_type_id: form.room_type_id,
      floor: form.floor.trim() || null,
      notes: form.notes.trim() || null,
    };

    if (editing) {
      updateRoom.mutate(
        { id: editing.id, updates: payload },
        { onSuccess: onClose },
      );
    } else {
      createRoom.mutate(
        { property_id: propertyId, ...payload },
        { onSuccess: onClose },
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h4 className="font-semibold text-gray-900">
            {editing ? 'Edit room' : 'New room'}
          </h4>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="rf-number"
                className="mb-1 block text-xs font-medium text-gray-700"
              >
                Room number *
              </label>
              <input
                id="rf-number"
                required
                value={form.room_number}
                onChange={(e) =>
                  setForm({ ...form, room_number: e.target.value })
                }
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                placeholder="e.g. 101"
              />
            </div>
            <div>
              <label
                htmlFor="rf-floor"
                className="mb-1 block text-xs font-medium text-gray-700"
              >
                Floor
              </label>
              <input
                id="rf-floor"
                value={form.floor}
                onChange={(e) => setForm({ ...form, floor: e.target.value })}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                placeholder="e.g. 1st"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="rf-type"
              className="mb-1 block text-xs font-medium text-gray-700"
            >
              Room type *
            </label>
            <DropdownSelect
              label="Room type"
              value={form.room_type_id}
              onChange={(v) => setForm({ ...form, room_type_id: v })}
              options={[
                { value: '', label: '— select type —' },
                ...roomTypes.map((rt) => ({ value: rt.id, label: rt.name })),
              ]}
              searchable={roomTypes.length > 10}
            />
          </div>

          <div>
            <label
              htmlFor="rf-notes"
              className="mb-1 block text-xs font-medium text-gray-700"
            >
              Notes
            </label>
            <textarea
              id="rf-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
              placeholder="e.g. Sea view, ground floor access"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
            >
              {isSubmitting ? 'Saving…' : editing ? 'Save changes' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
